import React, { useRef, useMemo, useLayoutEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Color, Fog } from 'three';
import { ROOM_COLORS } from '../../../game/phaserFacility/facilityLayout';
import { gridToWorld, ROOM_HEIGHTS } from './facilityCoords';
import { hourOfDay, sunStateForHour } from './dayNightCycle';

// Easing rate for the day/night transition (per second). Frame-rate independent
// via 1 - e^(-rate*dt), so the sun glides smoothly and holds when the game is paused.
const EASE_RATE = 2.5;

/**
 * Light rig for the 3D facility scene. Drives a smooth day/night cycle off the
 * sim clock: the directional "sun", ambient fill, and sky background all ease
 * toward the target for the current hour every frame.
 * @param {Array} unlockedRoomDefs - FACILITY_ROOMS entries that are not locked and not ghost
 * @param {number} tick - sim clock; 1 tick = 1 game hour
 */
export function FacilityLighting({ unlockedRoomDefs, tick = 0 }) {
  const sunRef = useRef();
  const ambientRef = useRef();
  const hemiRef = useRef();
  const { scene } = useThree();

  // Initialize the sky background once so the first frame isn't a flash of black.
  const initial = useMemo(() => sunStateForHour(hourOfDay(tick)), []); // eslint-disable-line react-hooks/exhaustive-deps
  const bgColor = useMemo(() => new Color(initial.fogColor), [initial.fogColor]);
  const didInitBg = useRef(false);
  const didInitFog = useRef(false);
  useLayoutEffect(() => {
    if (!didInitBg.current && scene.background == null) {
      scene.background = bgColor;
      didInitBg.current = true;
    }
    if (!didInitFog.current && !scene.fog) {
      scene.fog = new Fog(initial.fogColor, 28, 75);
      didInitFog.current = true;
    }
  }, [scene, bgColor, initial.fogColor]);

  // Scratch colors reused each frame to avoid per-frame allocations.
  const targetSun = useMemo(() => new Color(), []);
  const targetAmbient = useMemo(() => new Color(), []);
  const targetSky = useMemo(() => new Color(), []);
  const targetFog = useMemo(() => new Color(), []);
  const targetHemi = useMemo(() => new Color(), []);

  useFrame((_, delta) => {
    const target = sunStateForHour(hourOfDay(tick));
    const k = 1 - Math.exp(-EASE_RATE * delta);

    const sun = sunRef.current;
    if (sun) {
      targetSun.set(target.sunColor);
      sun.color.lerp(targetSun, k);
      sun.intensity += (target.sunIntensity - sun.intensity) * k;
      sun.position.x += (target.position[0] - sun.position.x) * k;
      sun.position.y += (target.position[1] - sun.position.y) * k;
      sun.position.z += (target.position[2] - sun.position.z) * k;
    }

    const amb = ambientRef.current;
    if (amb) {
      targetAmbient.set(target.ambientColor);
      amb.color.lerp(targetAmbient, k);
      amb.intensity += (target.ambientIntensity - amb.intensity) * k;
    }

    if (scene.fog) {
      targetFog.set(target.fogColor);
      scene.fog.color.lerp(targetFog, k);
    }

    const hemi = hemiRef.current;
    if (hemi) {
      targetHemi.set(target.horizonColor);
      hemi.color.lerp(targetHemi, k);
      hemi.groundColor.lerp(targetFog, k);
      const hemiIntensity = 0.18 + (1 - target.nightFactor) * 0.22;
      hemi.intensity += (hemiIntensity - hemi.intensity) * k;
    }

    if (scene.background && scene.background.isColor) {
      targetSky.set(target.fogColor);
      scene.background.lerp(targetSky, k);
    }
  });

  const night = sunStateForHour(hourOfDay(tick)).nightFactor;

  return (
    <>
      <hemisphereLight
        ref={hemiRef}
        args={[initial.horizonColor, initial.fogColor, 0.28]}
      />

      {/* Ambient fill — color/intensity animate with the day/night cycle */}
      <ambientLight
        ref={ambientRef}
        intensity={initial.ambientIntensity}
        color={initial.ambientColor}
      />

      {/* Sun — arcs across the sky, casts shadows */}
      <directionalLight
        ref={sunRef}
        position={initial.position}
        intensity={initial.sunIntensity}
        color={initial.sunColor}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-near={0.5}
        shadow-camera-far={80}
        shadow-camera-left={-25}
        shadow-camera-right={25}
        shadow-camera-top={25}
        shadow-camera-bottom={-25}
        shadow-bias={-0.001}
      />

      {/* Per-room interior glow — one point light above each unlocked room.
          These stay constant so the facility reads as "lit from within" at night. */}
      {unlockedRoomDefs.map(roomDef => {
        const colors = ROOM_COLORS[roomDef.type];
        if (!colors) return null;
        const { x, z } = gridToWorld(roomDef);
        const lightY = (ROOM_HEIGHTS[roomDef.type] || 1.2) + 0.3;
        return (
          <pointLight
            key={`light-${roomDef.id}`}
            position={[x, lightY, z]}
            color={colors.stroke}
            intensity={0.35 + night * 0.55}
            distance={Math.max(roomDef.w, roomDef.h) * 2.5}
            decay={2}
          />
        );
      })}
    </>
  );
}
